/**
 * Phase 32 — one channel's card, calm (user, 13/10/2026: intuitive, smooth):
 * the name and its switch, where the alerts go; once the channel is on, the
 * topics and the digests slide in. Telegram takes a chat id. Slack is one
 * shared channel, and this country is a thread in it (SlackFields).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Send } from 'lucide-react';
import type { DropGenNotificationSendResult } from '@trafficpulse/shared';

import { Switch } from '@/components/ui';

import { INPUT_CLASS } from '../../editor/general/fields/field-chrome';
import { DigestRows } from './DigestRows';
import { SlackFields } from './slack/SlackFields';
import { HowToGet } from './howto/HowToGet';
import { SlackLogo } from './logos/SlackLogo';
import { TelegramLogo } from './logos/TelegramLogo';
import type { ChannelDraft } from './notification-draft';
import { toggleTopic } from './notification-draft';
import { TopicChecklist } from './TopicChecklist';

const WORDS = {
  TELEGRAM: {
    name: 'Telegram',
    Icon: TelegramLogo,
    placeholder: 'Chat id, e.g. -1001234567890 (or @channel)',
  },
  SLACK: {
    name: 'Slack',
    Icon: SlackLogo,
  },
} as const;

const when = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Copenhagen' });
const SECTION = 'text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground';
const ENTER = 'duration-300 animate-in fade-in-0 slide-in-from-top-1 motion-reduce:animate-none';

export const ChannelCard: FC<{
  draft: ChannelDraft;
  countryName: string;
  recipientTimezone: string | null;
  defaultDigestAt: string;
  windowEnd: string;
  canManage: boolean;
  testing: boolean;
  testResult: DropGenNotificationSendResult | null;
  onChange: (patch: Partial<ChannelDraft>) => void;
  onTest: () => void;
}> = ({ draft, countryName, recipientTimezone, defaultDigestAt, windowEnd, canManage, testing, testResult, onChange, onTest }) => {
  const w = WORDS[draft.channel];
  const off = !canManage;
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border/60 bg-card p-4 transition-shadow duration-300" data-testid={`channel-${draft.channel.toLowerCase()}`}>
      <header className="flex items-center gap-2">
        <w.Icon className="h-5 w-5 shrink-0" />
        <span className="text-sm font-semibold">{w.name}</span>
        {draft.targetMasked ? (
          <span className="truncate font-mono text-[11px] text-muted-foreground" title="The saved target — type a new one below to replace it">
            {draft.targetMasked}
          </span>
        ) : null}
        <Switch className="ml-auto" checked={draft.enabled} disabled={off} onCheckedChange={(enabled) => onChange({ enabled })} aria-label={`${w.name} alerts on`} />
      </header>
      {draft.channel === 'SLACK' ? (
        <SlackFields draft={draft} countryName={countryName} disabled={off} onChange={onChange} />
      ) : (
        <div className="flex flex-col gap-1.5">
          <input
            type="text"
            value={draft.target}
            disabled={off}
            placeholder={draft.hasTarget ? 'keep the saved one' : WORDS.TELEGRAM.placeholder}
            onChange={(e) => onChange({ target: e.target.value })}
            className={`${INPUT_CLASS} h-8 w-full font-mono text-xs`}
            aria-label="Telegram target"
            title="Where the alerts go"
          />
          <HowToGet channel="TELEGRAM" />
        </div>
      )}
      {draft.enabled ? (
        <div className={`flex flex-col gap-3 ${ENTER}`} data-testid="channel-body">
          <div className="flex flex-col gap-1.5">
            <span className={SECTION}>Alerts</span>
            <TopicChecklist topics={draft.topics} disabled={off} onToggle={(t) => onChange({ topics: toggleTopic(draft.topics, t) })} />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className={SECTION}>Daily summaries</span>
            <DigestRows countryName={countryName} digestAt={draft.digestAt} dayEndDigest={draft.dayEndDigest} defaultDigestAt={defaultDigestAt} recipientTimezone={recipientTimezone} windowEnd={windowEnd} disabled={off} onChange={onChange} />
          </div>
        </div>
      ) : null}
      {draft.hasTarget && (draft.channel !== 'SLACK' || draft.hasThread) ? (
        <footer className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${ENTER}`}>
          <button
            type="button"
            disabled={off || testing}
            onClick={onTest}
            className="inline-flex items-center gap-1.5 rounded-md border border-input px-2.5 py-1 font-medium transition-colors hover:bg-muted active:scale-[0.98] disabled:opacity-50"
          >
            <Send className="h-3 w-3" /> {testing ? 'Sending…' : 'Send a test message'}
          </button>
          {testResult ? (
            <span className={`${ENTER} ${testResult.ok ? 'text-success' : 'text-destructive'}`}>{testResult.ok ? 'delivered' : `not delivered — ${testResult.error}`}</span>
          ) : null}
          <span className="ml-auto text-muted-foreground">
            {draft.lastError ? <span className="text-destructive">last delivery failed — {draft.lastError}</span> : draft.lastSentAt ? `last sent ${when(draft.lastSentAt)} 🇩🇰` : 'nothing sent yet'}
          </span>
        </footer>
      ) : null}
    </section>
  );
};
